import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../Avatar';
import { useNavigate } from 'react-router-dom';
import { useGetAllEntityRolesByUserId } from '@/hooks/user-entity-role.hooks';
import { Select } from '../Controls/Select';
import { TUserEntityRoleSchema } from '@/schemas/user-entity-role/user-entity-role.schema';
import { clearStorage, clearLocalCache } from '@/utils/storage.utils';
import { LANGUAGE } from '@/utils/constant.utils';
import { Button } from '../Button';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import { getPreparedEntityRoleLabel } from '@/utils/user-entity-role';
import './styles.css';

const Header = () => {
  const { i18n } = useTranslation();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [language, setLanguage] = useState('FR');
  const { loggedInUser, setLoggedInUser } = useAuth();

  const { hasNoRole, isModerator } = useUserRole();

  useEffect(() => {
    setLanguage('FR');
  }, []);

  const { data: entityRoles, isLoading: isLoadingEntityRoles } =
    useGetAllEntityRolesByUserId(loggedInUser?.id);

  const onLogout = () => {
    clearStorage();
    clearLocalCache();
    setLoggedInUser(undefined);
    navigate('/login');
  };

  const changeLng = (lang: string) => {
    setLanguage(lang);
    i18n.changeLanguage(lang.toLowerCase());
  };

  const getEntityRoleOptions = () => {
    if (
      !isLoadingEntityRoles &&
      entityRoles &&
      entityRoles.data &&
      entityRoles.data.length > 0
    ) {
      return entityRoles.data.map((role: TUserEntityRoleSchema) => {
        const roleType = role?.roleType?.name;
        const groups = [role?.group];
        const companies = [role?.company];
        const branches = [role?.branch];
        
        const { fullEntityRoleLabel } = getPreparedEntityRoleLabel({
          t,
          roleType,
          groups,
          companies,
          branches,
        });

        return {
          label: fullEntityRoleLabel,
          value: role?.id,
        };
      });
    }

    return undefined;
  };

  return (
    <header
      className="header fixed top-0 z-10 left-0 right-0 flex items-stretch shrink-0 bg-white-FEFEFE border-b border-b-gray-F1F0F3 dark-mode:bg-coal-600 dark-mode:border-b-coal-100"
      data-sticky="true"
      data-sticky-class="shadow-sm"
      data-sticky-name="header"
      id="header"
    >
      <div
        className="container-fixed flex justify-between items-stretch lg:gap-4"
        id="header_container"
      >
        {!isModerator && (
          <div className="flex gap-7 items-center">
            <Select
              selectClassName="header-custom-select"
              options={[
                {
                  value: 'DISTRIBUTOR',
                  label: t('distributor'),
                },
                {
                  value: 'BRANCH',
                  label: t('branch'),
                },
              ]}
            />
            <Select
              selectClassName="header-custom-select"
              options={getEntityRoleOptions()}
            />
          </div>
        )}
        <div className="flex gap-1 lg:hidden items-center -ml-1">
          <a className="shrink-0" href="/">
            <img
              className="max-h-[25px] w-full"
              src="/media/app/mini-logo.svg"
            />
          </a>
          <div className="flex items-center">
            <button
              className="btn btn-icon btn-light btn-clear btn-sm"
              data-drawer-toggle="#sidebar"
            >
              <i className="ki-filled ki-menu"></i>
            </button>
            <button
              className="btn btn-icon btn-light btn-clear btn-sm"
              data-drawer-toggle="#megamenu_wrapper"
            >
              <i className="ki-filled ki-burger-menu-2"></i>
            </button>
          </div>
        </div>
        <div className="flex items-stretch" id="megamenu_container"></div>
        <div className="flex items-center gap-8">
          {!hasNoRole && !isModerator && (
            <div>
              <Button
                text={t('requestAnInspection')}
                leftIcon={<i className="ki-outline ki-plus-circle" />}
                onClick={() => navigate('/vehicule-check/create')}
              />
            </div>
          )}
          <div className="menu" data-menu="true">
            <div
              className="menu-item"
              data-menu-item-offset="20px, 10px"
              data-menu-item-placement="bottom-end"
              data-menu-item-toggle="dropdown"
              data-menu-item-trigger="click|lg:click"
            >
              <Avatar
                togglesMenu
                imageUrl={loggedInUser?.profilePicture}
                containerClassName="w-9 h-9 rounded-full border-0 shadow-none hover:cursor-pointer"
              />
              <div className="menu-dropdown menu-default light:border-gray-300 w-full max-w-[271px]">
                <div className="flex items-center justify-between px-5 py-1.5 gap-1.5">
                  <div className="flex items-center gap-3">
                    <Avatar
                      imageUrl={loggedInUser?.profilePicture}
                      containerClassName="w-12 h-12 rounded-full border-0 shadow-0"
                    />
                    <div className="flex flex-col gap-1.5">
                      <span className="text-sm text-gray-800 font-semibold first-letter:uppercase leading-[1.05rem] line-clamp-2">
                        {loggedInUser?.firstname}{' '}
                        {loggedInUser?.lastname?.toUpperCase()}
                      </span>
                      <a
                        className="text-xs text-gray-600 font-medium hover:text-primary leading-[0.5rem]"
                        href={`/user/edit/${loggedInUser?.id}`}
                      >
                        {loggedInUser?.email}
                      </a>
                    </div>
                  </div>
                </div>
                <div className="menu-separator"></div>
                <div className="flex flex-col">
                  {!isModerator && (
                    <>
                      <div className="menu-item">
                        <a
                          className="menu-link"
                          href={`/user/edit/${loggedInUser?.id}`}
                        >
                          <span className="menu-icon">
                            <i className="ki-filled ki-profile-circle !text-[21px]"></i>
                          </span>
                          <span className="mt-0.5 menu-title !text-sm">
                            {t('common:myProfile')}
                          </span>
                        </a>
                      </div>
                      {/* <div className="menu-item">
                        <a className="menu-link" href="#">
                          <span className="menu-icon">
                            <i className="ki-filled ki-badge !text-[21px]"></i>
                          </span>
                          <span className="mt-0.5 menu-title !text-sm">
                            {t('common:myLucius')}
                          </span>
                        </a>
                      </div> */}
                    </>
                  )}
                  <div
                    className="menu-item"
                    data-menu-item-offset="-10px, 0"
                    data-menu-item-placement="left-start"
                    data-menu-item-toggle="dropdown"
                    data-menu-item-trigger="click|lg:hover"
                  >
                    <div className="menu-link">
                      <span className="menu-icon">
                        <i className="ki-filled ki-icon !text-[21px]"></i>
                      </span>
                      <span className="mt-0.5 menu-title !text-sm">
                        {t('common:language')}
                      </span>
                      <div className="flex items-center gap-1.5 rounded-md border border-gray-300 text-gray-600 p-1.5 text-2xs font-medium shrink-0">
                        FR
                        <img
                          alt=""
                          className="inline-block size-3.5 rounded-full"
                          src="/media/flags/france.svg"
                        />
                      </div>
                    </div>
                    <div className="menu-dropdown menu-default light:border-gray-300 w-full max-w-[170px]">
                      {LANGUAGE.map((lang) => (
                        <div
                          key={lang.lang}
                          className={`menu-item ${
                            language === lang.lang ? 'active' : ''
                          }`}
                          onClick={() => changeLng(lang.lang)}
                        >
                          <div className="menu-link h-10">
                            <span className="menu-icon">
                              <img
                                alt=""
                                className="inline-block size-4 rounded-full"
                                src={`/media/flags/${lang.flag}.svg`}
                              />
                            </span>
                            <span className="menu-title">{lang.lang}</span>
                            {language === lang.lang && (
                              <span className="menu-badge">
                                <i className="ki-solid ki-check-circle text-success text-base"></i>
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="menu-separator"></div>
                <div className="flex flex-col">
                  <div className="menu-item mb-0.5">
                    <div className="menu-link">
                      <span className="menu-icon">
                        <i className="ki-filled ki-moon !text-[21px]"></i>
                      </span>
                      <span className="menu-title !text-sm">
                        {t('common:darkMode')}
                      </span>
                      <label className="switch switch-sm">
                        <input
                          data-theme-state="dark"
                          data-theme-toggle="true"
                          name="check"
                          type="checkbox"
                          defaultValue={1}
                        />
                      </label>
                    </div>
                  </div>
                  <div className="menu-item px-4 py-1.5">
                    <div
                      className="btn btn-sm btn-light text-2sm justify-center"
                      onClick={onLogout}
                    >
                      {t('common:logout')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
